import { FlexibleColumn, GcTableColumn } from '@ip-only/front-end-library';
import {
  Domain,
  TableType,
} from '../../../entities/installation-reporting-entities';
import {
  baseColumns,
  columnModifications,
  mduCellTemplateIds,
} from './installation-reporting.columns';

export type ColumnModification = {
  column: FlexibleColumn;
  index?: number;
};

export type ColumnModifications = {
  domain: Record<Domain, ColumnModification[]>;
  tableType: Record<TableType, ColumnModification[]>;
};

function sortModsByIndex(
  mods: ColumnModification[],
  baseLength: number,
): ColumnModification[] {
  return mods.sort((a, b) => (a.index ?? baseLength) - (b.index ?? baseLength));
}

function insertColumns(
  result: FlexibleColumn[],
  mods: ColumnModification[],
  baseLength: number,
): FlexibleColumn[] {
  let insertsCounter = 0;

  for (const mod of mods) {
    const index = mod.index ?? baseLength;
    const position = index + insertsCounter;
    result.splice(position, 0, mod.column);
    insertsCounter++;
  }

  return result;
}

function applyMods(
  base: FlexibleColumn[],
  mods: ColumnModification[],
): FlexibleColumn[] {
  const result = [...base];
  const baseLength = base.length;
  const sortedMods = sortModsByIndex([...mods], baseLength);

  return insertColumns(result, sortedMods, baseLength);
}

function mapFlexibleToGc(column: FlexibleColumn): GcTableColumn {
  return {
    columnKey: column.id,
    headerText: column.header,
    alignment: column.alignment,
    cellTemplate:
      column.id === 'deliveryPoints' || mduCellTemplateIds.includes(column.id),
  };
}

function buildFlexibleColumns(
  domain: Domain,
  tableType: TableType = 'SDU',
): FlexibleColumn[] {
  const base = [...baseColumns];
  const domainMods: ColumnModification[] =
    columnModifications.domain[domain] ?? [];
  const tableTypeMods: ColumnModification[] =
    columnModifications.tableType[tableType] ?? [];
  const combinedMods = [...domainMods, ...tableTypeMods];

  return applyMods(base, combinedMods);
}

export function configureFlexibleColumns(
  domain: Domain,
  tableType: TableType = 'SDU',
): FlexibleColumn[] {
  return buildFlexibleColumns(domain, tableType);
}

export function configureGcTableColumns(
  domain: Domain,
  tableType: TableType = 'SDU',
): GcTableColumn[] {
  const flexibleColumns = buildFlexibleColumns(domain, tableType);
  return flexibleColumns.map(mapFlexibleToGc);
}
